import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Lock, Delete } from 'lucide-react';
import { useSettingsStore } from '../stores/settingsStore';

interface PinLockScreenProps {
  mode: 'unlock' | 'setup' | 'change';
  onSuccess: () => void;
  onCancel?: () => void;
}

const PinLockScreen: React.FC<PinLockScreenProps> = ({ mode, onSuccess, onCancel }) => {
  const { setPinCode, pinCode } = useSettingsStore();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState<'enter' | 'confirm'>('enter');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const isProcessingRef = useRef(false);

  const maxLength = 4;

  const handleNumberPress = (num: string) => {
    setError('');
    if (step === 'enter' && pin.length < maxLength) {
      setPin(prev => prev + num);
    } else if (step === 'confirm' && confirmPin.length < maxLength) {
      setConfirmPin(prev => prev + num);
    }
  };

  const handleDelete = () => {
    if (step === 'enter') {
      setPin(prev => prev.slice(0, -1));
    } else {
      setConfirmPin(prev => prev.slice(0, -1));
    }
  };

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  }, []);

  // Handle unlock mode - verify PIN matches stored PIN
  useEffect(() => {
    if (mode === 'unlock' && pin.length === maxLength && !isProcessingRef.current) {
      isProcessingRef.current = true;
      // Defer to next tick to avoid state update during render
      setTimeout(() => {
        if (pin === pinCode) {
          onSuccess();
        } else {
          setError('Incorrect PIN');
          triggerShake();
          setPin('');
        }
        isProcessingRef.current = false;
      }, 0);
    }
  }, [pin, mode, pinCode, onSuccess, triggerShake]);

  // Handle setup/change - move to confirm step
  useEffect(() => {
    if ((mode === 'setup' || mode === 'change') && step === 'enter' && pin.length === maxLength) {
      setTimeout(() => {
        setStep('confirm');
      }, 100);
    }
  }, [pin, mode, step]);

  // Handle setup/change - confirm PIN
  useEffect(() => {
    if ((mode === 'setup' || mode === 'change') && step === 'confirm' && confirmPin.length === maxLength && !isProcessingRef.current) {
      isProcessingRef.current = true;
      // Defer to next tick to avoid state update during render
      setTimeout(() => {
        if (pin === confirmPin) {
          setPinCode(pin);
          onSuccess();
        } else {
          setError('PINs do not match');
          triggerShake();
          setConfirmPin('');
          setStep('enter');
          setPin('');
        }
        isProcessingRef.current = false;
      }, 0);
    }
  }, [confirmPin, pin, mode, step, setPinCode, onSuccess, triggerShake]);

  const getTitle = () => {
    if (mode === 'unlock') return 'Enter PIN';
    if (mode === 'setup') return step === 'enter' ? 'Create PIN' : 'Confirm PIN';
    if (mode === 'change') return step === 'enter' ? 'New PIN' : 'Confirm New PIN';
    return 'Enter PIN';
  };

  const currentPin = step === 'enter' ? pin : confirmPin;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-yellow-100 to-yellow-50 z-50 flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="mb-8 text-center"
      >
        <div className="w-16 h-16 bg-yellow-400 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Lock className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">
          Releg<span className="font-mono text-yellow-600">.it</span>
        </h1>
      </motion.div>

      {/* Title */}
      <motion.h2
        key={getTitle()}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl font-semibold text-gray-700 mb-8"
      >
        {getTitle()}
      </motion.h2>

      {/* PIN Dots */}
      <motion.div
        animate={shake ? { x: [-10, 10, -10, 10, 0] } : {}}
        transition={{ duration: 0.4 }}
        className="flex gap-4 mb-8"
      >
        {[...Array(maxLength)].map((_, i) => (
          <div
            key={i}
            className={`w-4 h-4 rounded-full transition-all duration-200 ${
              i < currentPin.length
                ? 'bg-yellow-500 scale-110'
                : 'bg-gray-300'
            }`}
          />
        ))}
      </motion.div>

      {/* Error Message */}
      {error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-red-500 text-sm mb-4"
        >
          {error}
        </motion.p>
      )}

      {/* Number Pad */}
      <div className="grid grid-cols-3 gap-4 max-w-xs">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (
          <motion.button
            key={num}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleNumberPress(num)}
            className="w-16 h-16 rounded-full bg-white shadow-md flex items-center justify-center text-2xl font-semibold text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
          >
            {num}
          </motion.button>
        ))}
        
        {/* Cancel / Empty */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onCancel}
          className={`w-16 h-16 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
            onCancel ? 'text-gray-500 hover:bg-gray-100' : 'invisible'
          }`}
        >
          Cancel
        </motion.button>
        
        {/* 0 */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => handleNumberPress('0')}
          className="w-16 h-16 rounded-full bg-white shadow-md flex items-center justify-center text-2xl font-semibold text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
        >
          0
        </motion.button>
        
        {/* Delete */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={handleDelete}
          className="w-16 h-16 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
        >
          <Delete className="w-6 h-6" />
        </motion.button>
      </div>

      {/* Remove PIN option (only in change mode) */}
      {mode === 'change' && pinCode && (
        <button
          onClick={() => {
            setPinCode(null);
            onSuccess();
          }}
          className="mt-8 text-red-500 text-sm hover:underline"
        >
          Remove PIN Lock
        </button>
      )}
    </div>
  );
};

export default PinLockScreen;
